import { useEffect } from 'react';
import { getLessonById } from '../utils/lessonsService';

// Загружаем урок при изменении lessonId
export function useLessonLoader({
  lessonId, homeworkId, isStudent, lessonTitle, whiteboards,
  initialLessonBannerImage,
  setIsLoading, setLessonTitle, setDocBanners, setHomeworkBanners,
  setContainers, setDocContainers, setHomeworkContainers, setHomeworkDataCache,
  setWhiteboards, setEditorContents, setWhiteboardContents, setContainerComments,
  setInsertedRoundVideos, setInsertedRoundAudios, setInsertedRoundTexts,
  setBannerImage,
}) {
  useEffect(() => {
    if (lessonId && lessonId !== 'new') {
      setIsLoading(true);

      // Асинхронная загрузка урока из Supabase
      getLessonById(lessonId).then(lesson => {
        // console.log('Loading lesson:', lesson);

        if (lesson) {
        // Восстанавливаем содержимое редакторов
        let contentMap = {};
        if (lesson.editorContents && lesson.editorContents.length > 0) {
          lesson.editorContents.forEach(content => {
            contentMap[content.id] = content.content;
          });
        }

        // Восстанавливаем содержимое досок
        let whiteboardContentMap = {};
        if (lesson.whiteboardContents && lesson.whiteboardContents.length > 0) {
          lesson.whiteboardContents.forEach(content => {
            whiteboardContentMap[content.id] = content.content;
          });
        }

        // console.log('Editor content map:', contentMap);
        // console.log('Whiteboard content map:', whiteboardContentMap);

        // Устанавливаем все состояния одновременно
        setLessonTitle(lesson.title || '');

        // Восстанавливаем баннеры doc табов, фильтруя только base64 изображения
        const validDocBanners = {};
        if (lesson.docBanners) {
          Object.entries(lesson.docBanners).forEach(([docId, banner]) => {
            if (banner && banner.startsWith('data:image/')) {
              validDocBanners[docId] = banner;
            }
          });
        }
        setDocBanners(validDocBanners);

        // Загружаем баннеры домашних заданий
        const validHomeworkBanners = {};
        if (lesson.homeworkBanners) {
          Object.entries(lesson.homeworkBanners).forEach(([homeworkId, banner]) => {
            if (banner && banner.startsWith('data:image/')) {
              validHomeworkBanners[homeworkId] = banner;
            }
          });
        }
        setHomeworkBanners(validHomeworkBanners);

        setContainers(lesson.containers || [{ id: 1, type: 'editor' }]);
        setDocContainers(lesson.docContainers || {}); // Восстанавливаем контейнеры doc табов
        // homeworkContainers/homeworkDataCache are also bundled into the
        // lesson document (saveLesson, teacher-only) purely so a teacher's
        // unsent/draft homework template survives navigating away before
        // clicking Send. This restore must never run for a student: it's a
        // full replace (not a merge) of homeworkContainers, and the
        // lesson's copy wins ties in homeworkDataCache — so on every page
        // load/refresh it was stomping the student's real per-student
        // homework state (freshly loaded from the `homework` table by the
        // other effects) with whatever was in the teacher's own last lesson
        // save. That's exactly why a student's typed/submitted homework
        // kept reverting to "the teacher's last saved version".
        if (!isStudent) {
          setHomeworkContainers(lesson.homeworkContainers || {}); // Восстанавливаем контейнеры домашних заданий
          // Restore homework title / description / dueDate / bannerImage
          // metadata for any tabs that the teacher edited before clicking
          // Send — without this they'd come back to empty fields. Strip
          // containerContents/containers from each entry first: those hold
          // the actual per-student answer text, captured whenever the
          // teacher's browser last ran saveLesson (possibly mid-typing, long
          // before the student finished/submitted) — merging that stale
          // snapshot in seeds editorContents with old content before the
          // authoritative `homework` table fetch runs, and that fetch then
          // refuses to overwrite what looks like already-present content.
          // Same root cause as the editorContents restore above, just via
          // this second field.
          if (lesson.homeworkDataCache && typeof lesson.homeworkDataCache === 'object') {
            const sanitizedHomeworkDataCache = {};
            Object.entries(lesson.homeworkDataCache).forEach(([hwId, data]) => {
              if (!data || typeof data !== 'object') return;
              const { containerContents, containers, ...rest } = data;
              sanitizedHomeworkDataCache[hwId] = rest;
            });
            setHomeworkDataCache(prev => ({ ...prev, ...sanitizedHomeworkDataCache }));
          }
        }
        setWhiteboards(lesson.whiteboards || []);
        // contentMap comes from lesson.editorContents, which saveLesson
        // builds from a straight Object.entries(editorContents) dump — that
        // includes any homework-*-container-* keys the teacher's browser
        // happened to have in state at the moment of their last autosave
        // (e.g. from reviewing this exact student's homework mid-typing).
        // A full setEditorContents(contentMap) here plants that stale
        // snapshot into state before the authoritative per-student
        // `homework` table fetch (loadHomeworkData / the currentTab
        // effect, below) ever runs — and those fetches all guard against
        // overwriting non-empty content, so the stale lesson snapshot won
        // permanently. This was the actual cause of a teacher seeing only
        // "1" instead of "1, 2, 3" when reviewing a student's homework:
        // the lesson's last save happened to catch the container mid-typing.
        // Homework container content must always come from the `homework`
        // table, never from this shared lesson snapshot — exclude it here.
        // Merge, not replace: this must never discard homework-* keys that
        // another effect (loadHomeworkData / the currentTab effect) already
        // seeded with the correct content from the `homework` table —
        // whether that happened before or after this effect runs. A plain
        // setEditorContents(nonHomeworkContentMap) call here is a full
        // replace, so it would wipe out correctly-loaded homework content
        // the instant this effect fires, purely because the replacement
        // object has no homework-* keys in it at all.
        setEditorContents(prev => {
          const next = {};
          Object.entries(contentMap).forEach(([key, value]) => {
            if (!key.startsWith('homework-')) next[key] = value;
          });
          Object.keys(prev).forEach(key => {
            if (key.startsWith('homework-')) next[key] = prev[key];
          });
          return next;
        });
        setWhiteboardContents(whiteboardContentMap);
        setContainerComments(lesson.containerComments || {});
        setInsertedRoundVideos((lesson.insertedRoundVideos || []).map(v => ({ ...v, currentTime: 0, duration: 1, playing: false, overlayHovered: false })));
        setInsertedRoundAudios((lesson.insertedRoundAudios || []).map(a => ({ ...a, currentTime: 0, duration: 1, playing: false, overlayHovered: false })));
        setInsertedRoundTexts(lesson.insertedRoundTexts || []);

        // Проверяем валидность баннера и устанавливаем его
        if (lesson.bannerImage && lesson.bannerImage.startsWith('data:image/')) {
                  setBannerImage(lesson.bannerImage);
      } else {
        setBannerImage(initialLessonBannerImage);
      }

          // console.log('Lesson loaded:', lesson);
          // console.log('Editor contents loaded:', contentMap);
          // console.log('Whiteboard contents loaded:', whiteboardContentMap);
        }
        setIsLoading(false);
      }).catch(error => {
        console.error('Error loading lesson:', error);
        setIsLoading(false);
      });
    } else if (!homeworkId) {
      // Сбрасываем состояние для нового урока только если нет homeworkId
      // НО не сбрасываем whiteboards если они уже есть (чтобы не удалять только что добавленные)
      setLessonTitle('');
      setBannerImage(initialLessonBannerImage); // Сбрасываем баннер главного редактора
      setDocBanners({}); // Сбрасываем баннеры doc табов
      setHomeworkBanners({}); // Сбрасываем баннеры домашних заданий
      setContainers([{ id: 1, type: 'editor' }]);
      setDocContainers({}); // Сбрасываем контейнеры doc табов
      setHomeworkContainers({}); // Сбрасываем контейнеры домашних заданий
      // Не сбрасываем whiteboards здесь - они должны сохраняться при добавлении новых
      // setWhiteboards([]); // УБРАНО - не сбрасываем доски при добавлении новых
      setEditorContents({});
      setWhiteboardContents({});
      setContainerComments({});
      setIsLoading(false);
    } else {
      // Если есть homeworkId, не сбрасываем состояние, но устанавливаем базовые значения
      if (!lessonTitle) {
        setLessonTitle('Homework Review');
      }
      if (whiteboards.length === 0) {
        setContainers([{ id: 1, type: 'editor' }]);
      }
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, homeworkId]); // Убрали lessonTitle из зависимостей, чтобы не сбрасывать при вводе текста
}
